import { UploadOutlined } from '@ant-design/icons'
import { Button, Checkbox, Col, Form, Input, message, Modal, Row, Upload } from 'antd'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { ALLOWED_MIME_TYPES_IMAGE } from 'constants/mime-type'
import { logError } from 'utils/logger'

interface AddCustomPropertiesFieldModalProps {
  open: boolean
  onCancel: () => void
  onAdd: (field: { key: string; value: string; icon: string }) => void
}

const AddCustomPropertiesFieldModal = ({
  open,
  onCancel,
  onAdd,
}: AddCustomPropertiesFieldModalProps) => {
  const [createAnother, setCreateAnother] = useState(false)
  const [modalForm] = Form.useForm()
  const [iconUrl, setIconUrl] = useState<string>('')
  const [uploading, setUploading] = useState(false)

  const handleUploadIcon = async (file: File) => {
    const isAllowed = ALLOWED_MIME_TYPES_IMAGE.includes(file.type)
    if (!isAllowed) {
      message.error('Only image files (JPEG, PNG, GIF, BMP, WEBP, SVG) are allowed')
    } else {
      const formData = new FormData()
      formData.append('file', file)
      try {
        setUploading(true)
        const response = await AppAdmin.uploadSingleFile(formData)
        if (response.data.success) {
          setIconUrl(response.data.data.filePath)
          message.success('Icon uploaded successfully')
        }
      } catch (error: any) {
        message.error(error?.message || 'Icon upload failed')
        logError('Upload icon error:', error)
      } finally {
        setUploading(false)
      }
    }

    return false
  }

  const handleAddField = async () => {
    try {
      const values = await modalForm.validateFields()

      onAdd({ key: values.key, value: '', icon: iconUrl || '' })

      if (createAnother) {
        modalForm.resetFields()
        setIconUrl('')
      } else {
        onCancel()
      }
    } catch (error: any) {
      if (error?.errorFields) return
      message.error(error?.message || 'Failed to add field')
    }
  }

  const handleAfterOpenChange = () => {
    modalForm.resetFields()
    setIconUrl('')
  }

  return (
    <Modal
      title="Add Custom Field"
      open={open}
      onCancel={onCancel}
      centered
      destroyOnClose
      afterOpenChange={handleAfterOpenChange}
      footer={
        <Row justify="end" align="middle">
          <Col>
            <Checkbox checked={createAnother} onChange={(e) => setCreateAnother(e.target.checked)}>
              Create another
            </Checkbox>
          </Col>
          <Col>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleAddField}
              disabled={!iconUrl}
              className="add-custom-field-modal__add-btn"
            >
              Add Field
            </Button>
          </Col>
        </Row>
      }
    >
      <Form form={modalForm} layout="vertical" className="add-custom-field-modal__form">
        <Form.Item
          name="key"
          label="Field Name"
          rules={[{ required: true, message: 'Field name is required' }]}
        >
          <Input placeholder="Enter field name" />
        </Form.Item>

        <Form.Item label="Icon" required>
          <div className="add-custom-field-modal__icon-wrapper">
            <Upload accept="image/*,.svg" showUploadList={false} beforeUpload={handleUploadIcon}>
              <Button icon={<UploadOutlined />} loading={uploading}>
                {iconUrl ? 'Uploaded' : 'Upload Icon'}
              </Button>
            </Upload>
            {iconUrl && (
              <img
                src={iconUrl}
                alt="icon preview"
                className="add-custom-field-modal__icon-preview"
              />
            )}
          </div>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default AddCustomPropertiesFieldModal
